// File: src/pages/BlogDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// --- TOAST COMPONENT ---
const Toast = ({ message, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(onClose, 3000);
        return () => clearTimeout(timer);
    }, [onClose]);

    return (
        <div style={{
            position: 'fixed', bottom: '24px', left: '24px', zIndex: 10000,
            background: '#374151', color: 'white', padding: '12px 20px', borderRadius: '10px',
            display: 'flex', alignItems: 'center', gap: '10px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            fontSize: '14px', fontWeight: '500', animation: 'slideUpToast 0.3s ease-out'
        }}>
            <span style={{ color: '#4ADE80', fontSize: '18px' }}>✓</span>
            {message}
        </div>
    );
};

// Icons (Giữ nguyên)
const IconLike = ({ filled }) => <svg width="20" height="20" fill={filled ? "#EF4444" : "none"} stroke={filled ? "#EF4444" : "currentColor"} strokeWidth="2" viewBox="0 0 24 24"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>;
const IconShare = () => <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>;
const IconSend = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>;

export default function BlogDetailPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [commentText, setCommentText] = useState("");
    const [toast, setToast] = useState(null);
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

    const getUserId = () => {
        const token = localStorage.getItem('skillmatch_token');
        if (!token) return null;
        try { return JSON.parse(atob(token.split('.')[1])).user.id; } catch { return null; }
    };
    const userId = getUserId();

    const showToastMsg = (msg) => setToast(msg);

    // ✅ HELPER: Format ngày giờ chi tiết
    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('vi-VN', {
            hour: '2-digit', minute: '2-digit',
            day: '2-digit', month: '2-digit', year: 'numeric'
        });
    };

    useEffect(() => {
        const fetchBlog = async () => {
            try {
                const res = await fetch(`${apiBaseUrl}/api/blogs/${slug}`);
                if (res.ok) setBlog(await res.json());
                else showToastMsg("Bài viết không tồn tại hoặc đã bị xóa");
            } catch (e) { console.error(e); } 
            finally { setLoading(false); }
        };
        fetchBlog();
    }, [slug, apiBaseUrl]);

    const handleLike = async () => {
        if (!userId) return navigate('/login');
        try {
            const token = localStorage.getItem('skillmatch_token');
            const res = await fetch(`${apiBaseUrl}/api/blogs/${blog._id}/like`, {
                method: 'PUT',
                headers: { 'x-auth-token': token }
            });
            if (res.ok) {
                const newLikes = await res.json();
                setBlog({ ...blog, likes: newLikes });
            }
        } catch (e) { console.error(e); }
    };

    const handleComment = async (e) => {
        e.preventDefault();
        if (!userId) return navigate('/login');
        if (!commentText.trim()) return;

        try {
            const token = localStorage.getItem('skillmatch_token');
            const res = await fetch(`${apiBaseUrl}/api/blogs/${blog._id}/comment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
                body: JSON.stringify({ content: commentText })
            });
            if (res.ok) {
                const newComments = await res.json();
                setBlog({ ...blog, comments: newComments });
                setCommentText("");
                showToastMsg("Đã gửi bình luận!");
            }
        } catch (e) { console.error(e); }
    };

    const handleShare = () => {
        navigator.clipboard.writeText(window.location.href);
        showToastMsg("Đã sao chép liên kết bài viết!");
    };

    if (loading) return (
        <div className="min-h-screen pt-24 flex items-center justify-center bg-[#F8FAFC]">
            <span className="material-symbols-outlined animate-spin text-3xl text-blue-600">sync</span>
        </div>
    );
   
    if (!blog) return <div className="pt-24 text-center text-gray-500">Không tìm thấy bài viết</div>;

    const isLiked = blog.likes.includes(userId);

    return (
        <div className="bg-white min-h-screen pt-24 pb-12 font-sans">
            <div className="max-w-4xl mx-auto px-4">
                
                {/* Header */}
                <div className="mb-8">
                    <span className="text-blue-600 font-bold text-sm bg-blue-50 px-3 py-1 rounded-full uppercase">
                        {blog.industry}
                    </span>
                    <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-4 mb-4 leading-tight">
                        {blog.title}
                    </h1>
                    <div className="flex items-center justify-between text-gray-500 text-sm border-b border-gray-100 pb-6">
                        <div className="flex items-center gap-3">
                            <img 
                                src={blog.author.avatar || "https://api.dicebear.com/9.x/bottts-neutral/svg?seed=SkillMatchAI"} 
                                alt="Author" 
                                className="w-10 h-10 rounded-full bg-gray-100 object-cover"
                                // ✅ FIX: Nếu ảnh lỗi thì tự động dùng ảnh AI Robot
                                onError={(e) => e.target.src = "https://api.dicebear.com/9.x/bottts-neutral/svg?seed=SkillMatchAI"}
                            />
                            <div>
                                <p className="font-semibold text-gray-900">{blog.author.name}</p>
                                {/* ✅ FIX: Hiển thị ngày giờ chi tiết */}
                                <p>{formatDateTime(blog.publishedAt)}</p>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <span>👁️ {blog.views} lượt xem</span>
                            <span>💬 {blog.comments.length} thảo luận</span>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <article 
                    className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: blog.content }}
                />

                {/* Interaction Bar */}
                <div className="flex items-center gap-4 mt-10 pt-6 border-t border-gray-100">
                    <button 
                        onClick={handleLike}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-full border transition-all duration-200 cursor-pointer ${
                            isLiked 
                            ? 'border-red-200 bg-red-50 text-red-600' 
                            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300'
                        }`}
                    >
                        <IconLike filled={isLiked} /> 
                        <span className="font-semibold">{blog.likes.length} Thích</span>
                    </button>
                    
                    <button 
                        onClick={handleShare}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-full border border-gray-200 bg-white text-gray-600 font-semibold hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300 transition-all cursor-pointer"
                    >
                        <IconShare /> 
                        <span>Chia sẻ</span>
                    </button>
                </div>

                {/* Comments Section */}
                <div className="mt-12 bg-gray-50 p-6 rounded-2xl border border-gray-100">
                    <h3 className="text-xl font-bold mb-6 text-gray-900">Thảo luận cộng đồng ({blog.comments.length})</h3>
                    
                    <form onSubmit={handleComment} className="flex gap-3 mb-8">
                        <input 
                            type="text" 
                            className="flex-1 px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            placeholder="Chia sẻ ý kiến của bạn..."
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                        />
                        <button 
                            type="submit" 
                            className="bg-blue-600 text-white px-6 rounded-xl hover:bg-blue-700 transition-all flex items-center justify-center cursor-pointer shadow-lg shadow-blue-500/30"
                        >
                            <IconSend />
                        </button>
                    </form>

                    <div className="space-y-6">
                        {blog.comments.map((cmt, idx) => (
                            <div key={idx} className="flex gap-3 animate-fadeIn">
                                <img 
                                    src={cmt.user?.profile?.avatar || "https://cdn-icons-png.flaticon.com/512/149/149071.png"} 
                                    alt="User" 
                                    className="w-10 h-10 rounded-full border border-white shadow-sm object-cover"
                                    // ✅ FIX: Fallback ảnh user nếu lỗi
                                    onError={(e) => e.target.src = "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                                />
                                <div className="flex-1">
                                    <div className="bg-white px-4 py-3 rounded-2xl rounded-tl-none shadow-sm border border-gray-100">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-bold text-sm text-gray-900">
                                                {cmt.user?.profile?.fullName || "Người dùng ẩn danh"}
                                            </span>
                                            {cmt.user?.role === 'business' && (
                                                <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold border border-blue-200">
                                                    Doanh nghiệp
                                                </span>
                                            )}
                                            {/* ✅ FIX: Hiển thị ngày giờ chi tiết cho comment */}
                                            <span className="text-xs text-gray-400">• {formatDateTime(cmt.createdAt)}</span>
                                        </div>
                                        <p className="text-gray-700 text-sm leading-relaxed">{cmt.content}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {blog.comments.length === 0 && <p className="text-gray-400 text-center italic py-4">Chưa có bình luận nào. Hãy là người đầu tiên!</p>}
                    </div>
                </div>
            </div>

            {toast && <Toast message={toast} onClose={() => setToast(null)} />}
            
            <style>{`
                article h2 { font-size: 1.5rem; font-weight: 700; margin-top: 2rem; margin-bottom: 1rem; color: #111827; }
                article p { margin-bottom: 1.25rem; line-height: 1.7; }
                article ul { list-style-type: disc; padding-left: 1.5rem; margin-bottom: 1.25rem; }
                article li { margin-bottom: 0.5rem; }
                article strong { font-weight: 700; color: #111827; }
                @keyframes slideUpToast { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            `}</style>
        </div>
    );
}